import { AxiosError } from 'axios'
import type { QueryClient } from '@tanstack/react-query'
import { ApiRequestError } from '@workspace/react-query'
import { useAuthStore } from '@/stores/auth-store'
import { clearAssistantStorage } from '@/features/assistant/lib/assistant-storage'
import { authClient } from './auth'

/**
 * Two ways an offboarded user ends up hammering the API forever, both fixed here.
 *
 * The shell's interval pollers (notifications 15s, chat unread 15s, consent
 * capture 15s, shift-swaps 60s ×2, dashboard 2min) keep firing for as long as
 * the authenticated layout stays mounted. React Query's `retry` guard in
 * main.tsx does NOT stop them: `refetchInterval` is a scheduled poll, not a
 * retry, so the "don't retry 401/403" rule never applies to it. Nothing else
 * unmounted the shell, so the polling never ended.
 *
 *  - **403 `{ offboarded: true }`** — `requireAuth`
 *    (apps/api/src/middleware/auth.middleware.ts) rejects every request from a
 *    non-active member. The flag existed but nothing read it; main.tsx's 403
 *    branch was commented out.
 *  - **401** — `offboardMemberAction` deletes the user's sessions, so someone
 *    already signed in starts 401-looping instead. main.tsx ignores 401 *by
 *    design* ("let the user re-authenticate naturally"), which is right for a
 *    one-off but wrong for a stream that never recovers.
 */

/** 403 carrying the API's explicit "this member is not active" marker. */
export function isAccessRevokedError(error: unknown): boolean {
  if (error instanceof ApiRequestError) {
    return (
      error.status === 403 &&
      (error.body as { offboarded?: boolean } | undefined)?.offboarded === true
    )
  }

  if (error instanceof AxiosError) {
    return (
      error.response?.status === 403 &&
      (error.response?.data as { offboarded?: boolean } | undefined)
        ?.offboarded === true
    )
  }

  return false
}

export function isUnauthorizedError(error: unknown): boolean {
  if (error instanceof ApiRequestError) return error.status === 401
  if (error instanceof AxiosError) return error.response?.status === 401
  return false
}

/**
 * A 401 streak only counts as "the session is gone" when it is BOTH repeated and
 * sustained. The elapsed-time floor is the important half: a page load fires a
 * dozen queries at once, so a single bad moment (server restart, brief network
 * fault) trips a bare counter instantly and would sign people out spuriously.
 * Requiring the streak to span a real interval means only a poller that keeps
 * failing — the actual loop — reaches the threshold. At a 15s poll cadence that
 * lands at roughly 30s, while a simultaneous burst never does.
 */
const UNAUTHORIZED_STREAK_THRESHOLD = 3
const UNAUTHORIZED_STREAK_MIN_MS = 10_000

let unauthorizedCount = 0
let firstUnauthorizedAt = 0

/** Any successful query proves the session is alive — clear the streak. */
export function noteRequestSucceeded(): void {
  unauthorizedCount = 0
  firstUnauthorizedAt = 0
}

/** Records a 401 and reports whether the streak now warrants a teardown. */
export function shouldTearDownAfterUnauthorized(
  now: number = Date.now()
): boolean {
  unauthorizedCount += 1
  if (firstUnauthorizedAt === 0) firstUnauthorizedAt = now

  return (
    unauthorizedCount >= UNAUTHORIZED_STREAK_THRESHOLD &&
    now - firstUnauthorizedAt >= UNAUTHORIZED_STREAK_MIN_MS
  )
}

/**
 * Guards against re-entry: a screenful of queries can fail in the same tick, and
 * each one calls the teardown.
 */
let teardownStarted = false

/** Test-only escape hatch — this module's counters are deliberately sticky. */
export function resetSessionTeardownForTests(): void {
  teardownStarted = false
  unauthorizedCount = 0
  firstUnauthorizedAt = 0
}

/**
 * Ends the session and leaves the authenticated shell.
 *
 * Signing out is the load-bearing step, not the redirect. On the 403 path the
 * session cookie is still valid, so without it `requireAuth()` in
 * lib/middleware.ts accepts it on the very next navigation and drops the user
 * straight back into the loop.
 */
export async function tearDownSession(queryClient: QueryClient): Promise<void> {
  if (teardownStarted) return
  teardownStarted = true

  // Stop the bleeding first — cancel in-flight requests and drop every cached
  // query so nothing refetches while the sign-out round-trip is in the air.
  try {
    await queryClient.cancelQueries()
    queryClient.clear()
  } catch {
    // Best effort; the navigation below is what guarantees the pollers die.
  }

  // Local Havi transcripts go with the session, even if the sign-out fails.
  clearAssistantStorage()

  try {
    await authClient.signOut()
  } catch {
    // Already-dead session (the 401 path) or offline. Still clear local state
    // and leave — the API rejects this user on every route anyway.
  }

  useAuthStore.getState().auth.reset()

  // Full-document navigation, not a router.navigate(): it destroys every
  // interval timer, query observer and in-flight request in one step, with no
  // chance of a stale observer surviving the transition.
  window.location.replace('/sign-in')
}
